import type { SegmentAggregate, ValidationCell } from '../types'
import { wilsonInterval } from './confidence'

export function buildValidationPlan(aggregates: SegmentAggregate[]): ValidationCell[] {
  const cells: ValidationCell[] = []

  for (const agg of aggregates) {
    const [lo, hi] = agg.confidence.wilson
    const intervalWidth = hi - lo
    const needsValidation = intervalWidth > 0.3 || agg.confidence.band === 'Low' || agg.prediction_diversity > 0.3

    if (!needsValidation) continue

    // Sample size to narrow Wilson interval below 20pp at 95%
    const sampleSize = agg.segment_name.toLowerCase().includes('claim') ? 20 : 40

    cells.push({
      segment_id: agg.segment_id,
      segment_name: agg.segment_name,
      stimulus_id: agg.stimulus_id,
      stimulus_label: agg.stimulus_label,
      echo_prediction: agg.predicted_winner,
      echo_confidence: agg.confidence,
      sample_size_needed: sampleSize,
      reason: agg.confidence.weak_signals[0] ?? 'Low evidence coverage in this segment',
    })
  }

  return cells
}

export function simulateValidation(cells: ValidationCell[]): ValidationCell[] {
  return cells.map(cell => {
    // Insurance demo pattern: Message B confirmed overall, but recent claimants reveal gap
    const isClaimant = cell.segment_name.toLowerCase().includes('claim')
    const humanResult = cell.echo_prediction // Echo is correct overall
    const agreementScore = isClaimant ? 0.62 : 0.84

    const lessons = isClaimant
      ? [
          'Recent claimants need explicit claims-reassurance language the echo missed',
          'Add "Your claim matters" framing to Message B for this segment',
          'Update echo panel with 20 new claimant open-end responses',
        ]
      : [
          'Echo prediction confirmed — no major revisions needed',
          'Concrete premium explanation language validated',
        ]

    const [lo, hi] = wilsonInterval(
      Math.round(agreementScore * cell.sample_size_needed),
      cell.sample_size_needed
    )

    return {
      ...cell,
      human_result: humanResult,
      agreement_score: agreementScore,
      updated_confidence: {
        ...cell.echo_confidence,
        score: cell.echo_confidence.score * 0.95 + agreementScore * 0.05,
        wilson: [lo, hi],
        band: agreementScore >= 0.75 ? 'High' : agreementScore >= 0.55 ? 'Medium' : 'Low',
      },
      lessons,
    }
  })
}
